import {useCallback, useState} from 'react'
import {toast} from 'react-toastify'
import {Flow, FlowStatus, FlowPayload} from '../../WorkFlow/FlowTypes'
import {TOAST_POSITION, FLOW_COPY_SUFFIX, DEFAULT_SCOPE, ROUTES} from '../constants'

// Type for RTK Query mutation trigger - accepts any mutation that matches the signature
interface UseDuplicateFlowParams {
  flows: Flow[]
  createFlow: (
    payload: FlowPayload
  ) => {
    unwrap: () => Promise<{data: Flow}>
  }
  isAdmin: boolean
  navigate: (path: string, options?: {replace?: boolean; state?: unknown}) => void
  refetch: () => void
  refetchFlowStats: () => void
  t: (key: string) => string
  FlowStatus: typeof FlowStatus
  onCloseMenu?: () => void
}

export const useDuplicateFlow = ({
  flows,
  createFlow,
  isAdmin,
  navigate,
  refetch,
  refetchFlowStats,
  t,
  FlowStatus,
  onCloseMenu,
}: UseDuplicateFlowParams) => {
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null)

  const normalizeIndustry = (industry: string | string[] | undefined): string => {
    if (Array.isArray(industry) && industry.length > 0) {
      return industry[0]
    }
    if (industry && typeof industry === 'string') {
      return industry
    }
    return ''
  }

  const buildDuplicatePayload = (sourceFlow: Flow): FlowPayload => {
    return {
      name: `${sourceFlow.name}${FLOW_COPY_SUFFIX}`,
      scope: sourceFlow.scope || DEFAULT_SCOPE,
      industry: normalizeIndustry(sourceFlow.industry),
      affiliate: sourceFlow.affiliate,
      nodes: sourceFlow.nodes || [],
      edges: sourceFlow.edges || [],
      status: FlowStatus.INACTIVE,
    }
  }

  const handleDuplicateFlow = useCallback(
    async (id: string) => {
      let loadingToast: number | string | null = null

      try {
        setDuplicatingId(id)

        const sourceFlow = flows.find((flow) => flow._id === id)

        if (!sourceFlow) {
          toast.error(t('FLOWLIST.DUPLICATE_ERROR') || 'Flow not found', {
            position: TOAST_POSITION,
          })
          setDuplicatingId(null)
          return
        }

        loadingToast = toast.loading(t('FLOWLIST.DUPLICATING') || 'Duplicating flow...', {
          position: TOAST_POSITION,
        })

        const payload = buildDuplicatePayload(sourceFlow)
        const result = createFlow(payload)
        const duplicatedFlow = await result.unwrap()

        if (!duplicatedFlow?.data?._id) {
          throw new Error('Duplicated flow missing ID in response')
        }

        const newFlowId = duplicatedFlow.data._id


        if (onCloseMenu) {
          onCloseMenu()
        }

        if (loadingToast) {
          toast.dismiss(loadingToast)
        }

        toast.success(t('FLOWLIST.DUPLICATE_SUCCESS') || 'Flow duplicated successfully', {
          position: TOAST_POSITION,
        })

        refetch()
        refetchFlowStats()

        const editPath = isAdmin
          ? ROUTES.TEMPLATE_AUTOMATION_FLOW(newFlowId)
          : ROUTES.AUTOMATION_FLOW(newFlowId)

        navigate(editPath)
      } catch (error) {
        if (loadingToast) {
          toast.dismiss(loadingToast)
        }

        const errorMessage =
          (error as {data?: {message?: string}})?.data?.message ||
          t('FLOWLIST.DUPLICATE_ERROR') ||
          'Failed to duplicate flow'

        toast.error(errorMessage, {
          position: TOAST_POSITION,
        })
      } finally {
        setDuplicatingId(null)
      }
    },
    [flows, createFlow, isAdmin, navigate, refetch, refetchFlowStats, t, FlowStatus, onCloseMenu]
  )

  return {
    handleDuplicateFlow,
    duplicatingId,
    isDuplicating: duplicatingId !== null,
  }
}

